import React, { useEffect, useState, useRef } from 'react';
import { patientDocumentService, type PatientDocument } from '../../../services/patientService';
import { getPwaToken } from '../../../services/apiClient';
import Spinner from '../../../components/ui/Spinner';
import ConfirmModal from '../../../components/common/ConfirmModal';

interface PatientDocumentsTabProps {
  patientId: number;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function isPdf(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

function getFileIcon(mimeType: string): string {
  if (isImage(mimeType)) return 'fa-file-image';
  if (isPdf(mimeType)) return 'fa-file-pdf';
  return 'fa-file';
}

const PatientDocumentsTab: React.FC<PatientDocumentsTabProps> = ({ patientId }) => {
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewDocument, setViewDocument] = useState<PatientDocument | null>(null);
  const [viewObjectUrl, setViewObjectUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const docs = await patientDocumentService.list(patientId);
      setDocuments(docs);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cargar documentos');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [patientId]);

  useEffect(() => {
    return () => {
      if (viewObjectUrl) URL.revokeObjectURL(viewObjectUrl);
    };
  }, [viewObjectUrl]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('El archivo excede el tamaño máximo de 20 MB');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      await patientDocumentService.upload(patientId, file);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchDocuments();
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      await patientDocumentService.delete(patientId, deletingId);
      setDocuments(docs => docs.filter(d => d.id !== deletingId));
      setDeletingId(null);
    } catch (err: any) {
      console.error('Error al eliminar documento:', err);
    }
  };

  const handleView = async (doc: PatientDocument) => {
    if (!isImage(doc.mime_type) && !isPdf(doc.mime_type)) {
      window.open(doc.url, '_blank');
      return;
    }

    setViewDocument(doc);

    if (isPdf(doc.mime_type)) {
      // The browser's own PDF viewer inside an <iframe> doesn't reliably
      // carry the httpOnly auth cookie on some browsers, so a direct
      // <iframe src={doc.url}> silently renders nothing (images are fine
      // via plain <img src>, since the browser attaches the cookie
      // consistently there). Fetching through the same authenticated path
      // as every other request and handing the iframe a blob: URL sidesteps
      // that entirely.
      setViewLoading(true);
      setViewError(null);
      try {
        const token = await getPwaToken();
        const response = await fetch(doc.url, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        setViewObjectUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.error('Error al cargar el documento:', err);
        setViewError('No se pudo cargar el documento.');
      } finally {
        setViewLoading(false);
      }
    }
  };

  const closeViewer = () => {
    if (viewObjectUrl) URL.revokeObjectURL(viewObjectUrl);
    setViewObjectUrl(null);
    setViewDocument(null);
    setViewError(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner variant="inline" text="Cargando documentos..." size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <i className="fa fa-exclamation-triangle"></i> {error}
        <button className="btn btn-sm btn-outline-secondary ms-3" onClick={fetchDocuments}>
          <i className="fa fa-refresh"></i> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-8">
          <h4 style={{ margin: 0 }}>
            <i className="fa fa-folder-open"></i> Documentos ({documents.length})
          </h4>
        </div>
        <div className="col-md-4 text-end">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <button
            className="btn btn-sm btn-primary"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i> Subiendo...
              </>
            ) : (
              <>
                <i className="fa fa-upload"></i> Subir Documento
              </>
            )}
          </button>
        </div>
      </div>

      {uploadError && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setUploadError(null)}>&times;</button>
          {uploadError}
        </div>
      )}

      {documents.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="fa fa-folder-open fa-3x mb-3" style={{ opacity: 0.3 }}></i>
          <p>No hay documentos adjuntos para este paciente.</p>
          <p style={{ fontSize: 13 }}>
            Formatos permitidos: PDF, JPG, PNG, GIF, WebP, BMP, SVG (máx. 20 MB)
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th style={{ width: 50 }}></th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Tamaño</th>
                <th>Fecha</th>
                <th style={{ width: 120 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="text-center">
                    <i className={`fa ${getFileIcon(doc.mime_type)} fa-lg`}
                      style={{ color: isPdf(doc.mime_type) ? '#dc3545' : isImage(doc.mime_type) ? '#28a745' : '#6c757d' }}></i>
                  </td>
                  <td>
                    <span style={{ cursor: 'pointer', color: '#007bff' }}
                      onClick={() => handleView(doc)}
                      title="Ver documento">
                      {doc.original_name}
                    </span>
                    {doc.source && (
                      <small className="text-muted d-block">{doc.source}</small>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-secondary">
                      {doc.mime_type.split('/').pop()?.toUpperCase()}
                    </span>
                  </td>
                  <td>{formatFileSize(doc.file_size)}</td>
                  <td>{new Date(doc.created_at).toLocaleDateString('es-DO', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>
                    <button
                      className="btn btn-xs btn-outline-primary me-1"
                      onClick={() => handleView(doc)}
                      title="Ver"
                    >
                      <i className="fa fa-eye"></i>
                    </button>
                    <button
                      className="btn btn-xs btn-outline-danger"
                      onClick={() => setDeletingId(doc.id)}
                      title="Eliminar"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewDocument && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: '90vw', height: '90vh' }}>
            <div className="modal-content" style={{ height: '90vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`fa ${getFileIcon(viewDocument.mime_type)} mr-2`}></i>
                  {viewDocument.original_name}
                </h5>
                <button type="button" className="close" onClick={closeViewer}>&times;</button>
              </div>
              <div className="modal-body p-0" style={{ flex: 1, overflow: 'hidden' }}>
                {isImage(viewDocument.mime_type) ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#1a1a1a' }}>
                    <img
                      src={viewDocument.url}
                      alt={viewDocument.original_name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : viewLoading ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <Spinner variant="inline" text="Cargando documento..." size="md" />
                  </div>
                ) : viewError ? (
                  <div className="d-flex align-items-center justify-content-center h-100 text-danger">
                    <i className="fa fa-exclamation-triangle me-2"></i> {viewError}
                  </div>
                ) : viewObjectUrl ? (
                  <iframe
                    src={viewObjectUrl}
                    title={viewDocument.original_name}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        show={deletingId !== null}
        title="Eliminar documento"
        body="¿Está seguro que desea eliminar este documento? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={false}
        onConfirm={handleDelete}
        onCancel={() => setDeletingId(null)}
      />
    </div>
  );
};

export default PatientDocumentsTab;
